
import Vue from "vue";
import PreferencesPanel from "@/components/Settings/Preferences/PreferencesPanel.vue";
import { Component } from "vue-property-decorator";
import {
  closeAllUiPanels,
  getPanelPositionPreferenceFromLocalStorage,
  openUiPanel,
  resetUiPanelPositionsToDefault,
  savePanelPositionPreferenceToLocalStorage,
  uiPanelDefaultPositions
} from "@/lib/panels/panels";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import { PanelPosition, UIPanel, UISettings } from "@/types/ui";
import {
  setScaleToFitAndGetSettings,
  UI_SETTINGS_LOCAL_STORAGE_KEY,
  uiSettingsDefaults
} from "@/lib/ui-preference/ui-preference";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import { uiStore } from "@/store/store-accessor";
import defensiveCopy from "@/util/defensive-copy";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { writeToLocalStorage } from "@/util/localstorage";
import NiceButton from "@/components/UI/NiceButton.vue";
import isEqual from "lodash/isEqual";
import { isBreakdanceDevelopmentEnvironment } from "@/lib/dev/development-environment";

@Component({
  components: { NiceButton, PreferencesPanel, ButtonBarInput, ToggleInput }
})
export default class CustomUiSettings extends Vue {
  panelsState = this.getPanelsInitialState();

  getPanelsInitialState() {
    const panels: { id: UIPanel; label: string }[] = [
      {
        id: "add",
        label: "Add"
      },
      {
        id: "elementproperties",
        label: "Element Properties"
      },
      {
        id: "selectors",
        label: "Selectors"
      },
      {
        id: "globalSettings",
        label: "Global Settings"
      },
      {
        id: "structure",
        label: "Structure"
      },
      {
        id: "history",
        label: "History & Revisions"
      }
    ];

    return panels.map(panel => ({
      ...panel,
      value: getPanelPositionPreferenceFromLocalStorage(panel.id)
    }));
  }

  setPanelPosition(panel: UIPanel, position: PanelPosition) {
    const index = this.panelsState.findIndex(
      panelState => panelState.id === panel
    );

    this.panelsState[index].value = position;

    savePanelPositionPreferenceToLocalStorage(panel, position);
    closeAllUiPanels();
    openUiPanel(panel);
  }

  resetUiPanelPositionsToDefault() {
    resetUiPanelPositionsToDefault();

    this.panelsState = this.getPanelsInitialState();
  }

  get arePanelPositionsDefault() {
    return isEqual(
      uiPanelDefaultPositions,
      this.panelsState.reduce((acc: Record<string, PanelPosition>, panel) => {
        acc[panel.id] = panel.value;
        return acc;
      }, {})
    );
  }

  get panelPositionsAsItems() {
    return ["left", "right"].map(x => {
      return {
        text: x,
        value: x
      };
    });
  }

  get popoutPositionItems() {
    return [
      {
        text: "Stacked",
        value: "STACKED"
      },
      {
        text: "Stacked (First Visible)",
        value: "STACKED_FIRST_VISIBLE"
      },
      {
        text: "Side By Side",
        value: "SIDE_BY_SIDE"
      }
    ];
  }

  get displayShowExperimentalElementsSetting() {
    return isBreakdanceDevelopmentEnvironment();
  }

  get isDarkMode() {
    return uiStore.settings.theme === "dark";
  }

  get isScaledToFit() {
    return uiStore.settings.scaleToFit === "SCALED_TO_FIT";
  }

  get isShowingIdsInStructurePanel() {
    return uiStore.settings.showElementIdInStructurePanel;
  }

  get isTinyMcePreferredForRichText() {
    return uiStore.settings.isTinyMcePreferredForRichText;
  }

  setScaleToFit(scaleToFit: boolean) {
    this.setUiSettings(setScaleToFitAndGetSettings(scaleToFit));
  }

  toggleTheme() {
    const settings = defensiveCopy(uiStore.settings);
    settings.theme = this.isDarkMode ? "light" : "dark";

    this.setUiSettings(settings);
  }

  setDisableNoticeForEmptySSRElements(disable: boolean) {
    const settings = defensiveCopy(uiStore.settings);
    settings.disableNoticeForEmptySSRElements = disable;

    this.setUiSettings(settings);
  }

  get disableNoticeForEmptySSRElements() {
    return uiStore.settings.disableNoticeForEmptySSRElements;
  }

  get showExperimentalElements() {
    return uiStore.settings.showExperimentalElements;
  }

  setShowExperimentalElements(value: boolean) {
    const settings = defensiveCopy(uiStore.settings);
    settings.showExperimentalElements = value;

    this.setUiSettings(settings);
  }

  setIsShowingIdsInStructurePanel(value: boolean) {
    const settings = defensiveCopy(uiStore.settings);
    settings.showElementIdInStructurePanel = value;

    this.setUiSettings(settings);
  }

  get disableCodeMirrorLineWrapping() {
    return uiStore.settings.disableCodeMirrorLineWrapping;
  }

  setDisableCodeMirrorLineWrapping(value: boolean) {
    const settings = defensiveCopy(uiStore.settings);
    settings.disableCodeMirrorLineWrapping = value;
    this.setUiSettings(settings);
  }

  setIsTinyMcePreferredForRichText(value: boolean) {
    const settings = defensiveCopy(uiStore.settings);
    settings.isTinyMcePreferredForRichText = value;
    this.setUiSettings(settings);
  }

  get autoScrollEnabled() {
    return uiStore.settings.autoScrollEnabled;
  }

  get popoutPosition() {
    return uiStore.settings.popoutPosition;
  }

  setAutoScrollEnabled(value: boolean) {
    const settings = defensiveCopy(uiStore.settings);
    settings.autoScrollEnabled = value;

    this.setUiSettings(settings);
  }

  setPopoutPosition(value: string) {
    const settings = defensiveCopy(uiStore.settings);
    settings.popoutPosition = value;

    this.setUiSettings(settings);
  }

  setUiSettings(settings: UISettings) {
    const maybeSettingsAsJsonString = jsonStringifyOrUndefined({
      ...uiSettingsDefaults,
      ...settings
    });
    if (maybeSettingsAsJsonString) {
      writeToLocalStorage(
        UI_SETTINGS_LOCAL_STORAGE_KEY,
        maybeSettingsAsJsonString
      );
    }

    uiStore.setSettings(settings);
  }
}
